/**
 * THE ALLOWLIST — flags whose last ONLINE decision is replayed while the flag
 * provider is unreachable, instead of collapsing to their hardcoded default.
 *
 * Everything NOT named here keeps today's behaviour: an unreachable provider
 * resolves the flag to its default. That is the conservative case and it stays
 * the case for every flag by default.
 *
 * ## An allowlist, deliberately not "cache every flag"
 *
 * A cached decision is an answer given up to seven days ago that becomes
 * authoritative at the one moment nobody can correct it. The set of flags that
 * gets that treatment has to be one a reviewer can read in full. Caching every flag
 * would extend it silently to flags nobody weighed, and two classes make that
 * actively unsafe:
 *
 *   - **Kill switches.** A kill switch exists to change behaviour faster than a
 *     release can. Caching one means a machine that went offline before the
 *     switch was pulled keeps running the behaviour it was pulled for, for up
 *     to a week.
 *   - **Destructive and safety gates.** Where the hardcoded default is the
 *     deliberately cautious answer — no deletion, no discard, keep the
 *     confirmation — replacing it with a stale `true` inverts the caution
 *     exactly when the app is least able to recover.
 *
 * ## The bar for adding a key
 *
 * 1. The flag gates a surface a clinician can reach WHILE OFFLINE, and
 * 2. replaying a stale `true` for up to seven days is safe on its own, and
 * 3. the flag gates nothing destructive or irreversible. A layout kill switch
 *    may be replayed only when preserving the last-rendered layout is safer
 *    than switching trees mid-consult, and its offline limitation is recorded.
 *
 * Fail any of the three and the flag stays out, whatever the UI cost.
 *
 * ## What is in, and why
 *
 * Both entries gate the same offline-reachable surface — the session list and
 * the session detail page you open from it. The reported failure: with V2 on
 * and opted into, going offline dropped an in-progress consult back to V1,
 * because both keys resolved to their `false` defaults the moment Flagsmith
 * became unreachable. Neither gates anything destructive; both only choose
 * which layout renders over identical session data, so a stale `true` shows the
 * clinician the layout they were already using.
 *
 * Every key here is written by `OpenFeatureContextSync` on each provider settle
 * and read back only through `useCachedFlagFallback`.
 */
import { NEW_SESSION_LIST_FLAG_KEY } from '@heidi/session/flags';

import { SESSION_PAGE_V2_FLAG_KEY } from './session-page-v2-flag';

// UNCARVED DOMAIN KEY (app-shell, DSK-748): not a cache identity at all — these are
// Flagsmith feature-flag keys, each minted once elsewhere, merely listed here. No
// queryKey, no mutationKey, and the localStorage key the decision cache derives is
// built in `offline-recording-flag-cache.ts` from the flag key plus its own prefix,
// not from this array. There is no /contracts door for a flag-key list, and flags are
// app-shell: `src/features/flags` is the single canonical surface for them.
// eslint-disable-next-line @heidi/rules/no-new-cache-key-declaration -- feature-flag keys listed for the offline decision cache, not a cache identity; each key is minted once elsewhere and nothing is keyed on this array
export const OFFLINE_CACHED_FLAG_KEYS = [
  NEW_SESSION_LIST_FLAG_KEY,
  SESSION_PAGE_V2_FLAG_KEY,
] as const;

export type OfflineCachedFlagKey = (typeof OFFLINE_CACHED_FLAG_KEYS)[number];
